/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta

// Función para crear un comentario en un blog
const createComment = async ({ blogId, commentData }: { blogId: string; commentData: { content: string } }) => {
  const response = await axiosInstance.post(`/comments/${blogId}`, commentData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear un comentario en un blog
export const useCreateComment = () => {
  const mutation = useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      toast.success('Comentario creado con éxito.');
    },
    onError: (error: any) => {
      // Captura el mensaje de error exacto
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      console.error('Error al crear el comentario:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
    error: mutation.error?.response?.data?.message || mutation.error?.message, // Captura el error correctamente
  };
};



// Función para obtener todos los comentarios con paginación para un blog específico
const fetchAllComments = async ({ blogId, page, limit }: { blogId: string; page: number; limit: number }) => {
  const response = await axiosInstance.get(`/comments/comments/${blogId}`, {
    params: { page, limit },
  });
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar todos los comentarios de un blog específico con paginación
export const useGetAllComments = (blogId: string, page: number, limit: number) => {
  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['comments', blogId, page, limit], // Ahora el blogId es parte de la clave de la consulta
    queryFn: () => fetchAllComments({ blogId, page, limit }), // Pasa blogId junto con page y limit
    refetchOnWindowFocus: true, // Volver a obtener datos al cambiar el focus
    refetchInterval: 600000, // Refetch cada 1 minuto (60,000ms)
  });

  return {
    comments: data?.comments,
    total: data?.total,
    isLoading,
    isError,
    error,
    refetch, // Devuelve la función para refetch manual si se necesita
  };
};



// Función para obtener los comentarios de un usuario
const fetchUserComments = async () => {
  const response = await axiosInstance.get('/comments/user/comments');
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para listar los comentarios de un usuario
export const useGetUserComments = () => {
  const { data, error, isLoading, isError } = useQuery({
    queryKey: ['user-comments'], // Identifica la consulta en React Query
    queryFn: async () => {
      try {
        return await fetchUserComments();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (error as any).response?.data?.message || 'Error desconocido';
        toast.error(`Error al obtener los comentarios del usuario: ${errorMessage}`);
        console.error('Error al obtener los comentarios del usuario:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Evita que los datos se vuelvan a obtener al cambiar de ventana
  });

  return {
    userComments: data,  // Lista de comentarios del usuario obtenidos
    isLoading,            // Estado de carga
    isError,              // Indica si hubo un error
    error,                // Detalle del error (si existe)
  };
};


// Función para eliminar un comentario
const deleteComment = async (commentId: string) => {
  const response = await axiosInstance.delete(`/comments/${commentId}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para eliminar un comentario
export const useDeleteComment = () => {
  const mutation = useMutation({
    mutationFn: deleteComment, // Función para eliminar el comentario
    onSuccess: () => {
      toast.success('Comentario eliminado con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error desconocido';
      toast.error(`Error al eliminar el comentario: ${errorMessage}`);
      console.error('Error al eliminar el comentario:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
  };
};


// Función para actualizar un comentario
const updateComment = async ({
  commentId,
  commentData,
}: {
  commentId: string;
  commentData: any;
}) => {
  const response = await axiosInstance.put(`/comments/${commentId}`, commentData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para actualizar un comentario
export const useUpdateComment = () => {
  const mutation = useMutation({
    mutationFn: updateComment, // Función para actualizar el comentario
    onSuccess: () => {
      toast.success('Comentario actualizado con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error desconocido';
      toast.error(`Error al actualizar el comentario: ${errorMessage}`);
      console.error('Error al actualizar el comentario:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
  };
};




// Función para crear una respuesta a un comentario
const createReply = async ({
  commentId,
  replyData,
}: {
  commentId: string;
  replyData: any;
}) => {
  const response = await axiosInstance.post(`/comments/${commentId}/reply`, replyData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear una respuesta a un comentario
export const useCreateReply = () => {
  const mutation = useMutation({
    mutationFn: createReply, // Función para crear la respuesta
    onSuccess: () => {
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error desconocido';
      console.error('Error al crear la respuesta:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la respuesta fue creada con éxito
  };
};



// Función para obtener las respuestas de un comentario
const fetchReplies = async (commentId: string) => {
  try {
    const response = await axiosInstance.get(`/comments/${commentId}/replies`);
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    // Mejor manejo de errores en la función
    const errorMessage = error?.response?.data?.message || 'Error desconocido al obtener las respuestas';
    toast.error(errorMessage);
    console.error('Error al obtener las respuestas:', errorMessage);
    throw new Error(errorMessage); // Propaga el error para que React Query lo maneje
  }
};

// Hook para obtener las respuestas de un comentario
export const useGetReplies = (commentId: string) => {
  const { data, error, isLoading, isError, refetch } = useQuery({
    queryKey: ['replies', commentId], // Identifica la consulta en React Query
    queryFn: () => fetchReplies(commentId), // Función que obtiene las respuestas
    staleTime: 5 * 60 * 1000, // Tiempo en milisegundos antes de considerar la consulta como "stale" (5 minutos)    retry: 2, // Intentar nuevamente la consulta en caso de error
    refetchOnWindowFocus: false, // No volver a hacer la consulta al enfocarse en la ventana
  });

  // Opcional: puedes exponer la función `refetch` si se desea manualmente refetch de las respuestas
  return {
    replies: data,        // Respuestas obtenidas
    isLoading,            // Estado de carga
    isError,              // Indica si hubo un error
    error,                // Detalle del error (si existe)
    refetch,              // Función para refetch manual
    isSuccess: !isError && !isLoading, // Para saber si la consulta fue exitosa
  };
};



// Función para eliminar una respuesta
const deleteReply = async ({ commentId, replyId }: { commentId: string; replyId: string }) => {
  const response = await axiosInstance.delete(`/comments/${commentId}/replies/${replyId}`);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para eliminar una respuesta
export const useDeleteReply = () => {
  const mutation = useMutation({
    mutationFn: deleteReply, // Función para eliminar la respuesta
    onSuccess: () => {
      toast.success('Respuesta eliminada con éxito.');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al eliminar la respuesta: ${errorMessage}`);
      console.error('Error al eliminar la respuesta:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
  };
};


// Función para actualizar una respuesta
const updateReply = async ({
  commentId,
  replyId,
  replyData,
}: {
  commentId: string;
  replyId: string;
  replyData: any;
}) => {
  const response = await axiosInstance.put(
    `/comments/${commentId}/replies/${replyId}`,
    replyData
  );
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para actualizar una respuesta
export const useUpdateReply = () => {
  const mutation = useMutation({
    mutationFn: updateReply, // Función para actualizar la respuesta
    onSuccess: () => {
      toast.success('Respuesta actualizada con éxito.');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al actualizar la respuesta: ${errorMessage}`);
      console.error('Error al actualizar la respuesta:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la actualización fue exitosa
  };
};
